import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Subscription, filter, first } from 'rxjs';

import { BlockUIModule } from 'primeng/blockui';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';

// Composants
import { AgentMarkerComponent } from 'src/app/@shared/components/agent-marker/agent-marker.component';
import { AccountEntryLinesPanelComponent } from '../../components/account-entry-lines-panel/account-entry-lines-panel.component';
import {
    AnnouncementDialogComponent,
    AnnouncementDialogData,
} from 'src/app/announcements/components/announcement-dialog/announcement-dialog.component';
import { AssistantWorkspaceComponent } from 'src/app/ai/components/assistant-workspace/assistant-workspace.component';
import { ConversationIndicatorComponent } from "../../../messaging/components/conversations/conversation-indicator/conversation-indicator.component";
import { DocumentationHelpIndicatorComponent } from "../../../documentation/components/documentation-help-indicator/documentation-help-indicator.component";
import { HeaderComponent } from '../../components/header/header.component';
import { ObjectHistoryComponent } from 'src/app/@shared/components/object-history/object-history.component';
import {
    LegalConsentDialogComponent,
    LegalConsentDialogData,
} from 'src/app/legal/components/legal-consent-dialog/legal-consent-dialog.component';
import { SideMenuComponent } from '../../components/side-menu/side-menu.component';

// Services
import { AnnouncementService } from 'src/app/@core/services/announcements/announcement.service';
import { AppService } from 'src/app/@core/services/app.service';
import { AssistantPanelService } from 'src/app/@core/services/ai/assistant-panel.service';
import { DialogConfigService } from 'src/app/@core/services/dialog-config.service';

// Enums
import { LegalDocumentTypeEnum } from 'src/app/@shared/enums/legal-document-type.enum';
import { LegalStatusEnum } from 'src/app/@shared/enums/legal-status.enum';

// Models
import { Announcement } from 'src/app/@shared/models/announcements/announcement.model';
import { User } from 'src/app/@shared/models/user/user.model';

@Component({
    selector: 'app-shell',
    imports: [
        CommonModule,
        RouterModule,
        BreadcrumbModule,
        ButtonModule,
        HeaderComponent,
        SideMenuComponent,
        BlockUIModule,
        ProgressSpinnerModule,
        TooltipModule,
        ConversationIndicatorComponent,
        DocumentationHelpIndicatorComponent,
        AccountEntryLinesPanelComponent,
        AgentMarkerComponent,
        AssistantWorkspaceComponent,
    ],
    templateUrl: './shell.component.html',
    styleUrls: ['./shell.component.scss']
})
export class ShellComponent implements OnInit, OnDestroy {
    appService = inject(AppService);
    assistantPanelService = inject(AssistantPanelService);
    private _announcementService = inject(AnnouncementService);
    private _dialogConfigService = inject(DialogConfigService);
    private _subscriptions = new Subscription();

    /*----------------------------------------------------------------------------------------
     * Lifecycle
     ----------------------------------------------------------------------------------------*/

    ngOnInit(): void {
        this._subscriptions.add(
            this.appService.userLoaded$
                .pipe(filter(user => !!user))
                .subscribe(user => this._checkUserLegalStatus(user!))
        );
    }

    ngOnDestroy(): void {
        this._subscriptions.unsubscribe();
    }

    /*----------------------------------------------------------------------------------------
     * Accesseurs
     ----------------------------------------------------------------------------------------*/

    public get menuWidthClass(): string {
        return this.appService.menuOpen ? 'menu-open' : 'menu-closed';
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    public onAgentMarkerCleared(): void {
        this.appService.agentMarkerTarget?.onCleared();
    }

    public onAgentMarkerUndone(): void {
        this.appService.agentMarkerTarget?.onUndone();
    }

    public onClickHistory(): void {
        this._openHistoryDialog();
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    /**
     * Ouvre la timeline d'historique de l'objet consulté sur la page courante
     */
    private _openHistoryDialog(): void {
        const target = this.appService.historyTarget;
        if (!target) {
            return;
        }
        this._dialogConfigService.show(ObjectHistoryComponent, 'OBJECT_HISTORY_DIALOG', {
            source: target.source,
            service: target.service,
        });
    }

    private _checkUserLegalStatus(user: User): void {
        if (user.cguStatus === LegalStatusEnum.KO || user.cguStatus === LegalStatusEnum.OBSOLETE) {
            const mode = user.cguStatus === LegalStatusEnum.KO ? 'blocking' : 'optional';
            this._openLegalDialogAndWait(LegalDocumentTypeEnum.CGU, mode, undefined, (accepted) => {
                if (accepted && this.appService.user) {
                    this.appService.user.cguStatus = LegalStatusEnum.OK;
                }

                // Contrôle de la confidentialité après le CGU, pour éviter d'afficher les deux modales en même temps
                if (user.privacyStatus === LegalStatusEnum.KO || user.privacyStatus === LegalStatusEnum.OBSOLETE) {
                    this._openLegalDialogAndWait(LegalDocumentTypeEnum.PRIVACY, mode, undefined, (acceptedPrivacy) => {
                        if (acceptedPrivacy && this.appService.user) {
                            this.appService.user.privacyStatus = LegalStatusEnum.OK;
                        }
                        this._loadAnnouncements();
                    });
                } else {
                    this._loadAnnouncements();
                }
            });
        } else if (user.privacyStatus === LegalStatusEnum.KO || user.privacyStatus === LegalStatusEnum.OBSOLETE) {
            const mode = user.privacyStatus === LegalStatusEnum.KO ? 'blocking' : 'optional';
            this._openLegalDialogAndWait(LegalDocumentTypeEnum.PRIVACY, mode, undefined, (accepted) => {
                if (accepted && this.appService.user) {
                    this.appService.user.privacyStatus = LegalStatusEnum.OK;
                }
                this._loadAnnouncements();
            });
        } else {
            this._loadAnnouncements();
        }
    }

    /**
     * Chargement des annonces après le contrôle légal : les modales légales passent en
     * premier, pour ne jamais superposer deux dialogs au démarrage.
     */
    private _loadAnnouncements(): void {
        this._announcementService.loadAll$().subscribe(() => {
            this._showNextAnnouncement(this._announcementService.toPopup);
        });
    }

    private _openLegalDialogAndWait(
        type: LegalDocumentTypeEnum,
        mode: LegalConsentDialogData['mode'],
        clientId: number | undefined,
        onClose: (accepted: boolean) => void
    ): void {
        const data: LegalConsentDialogData = { documentType: type, mode, clientId };
        const uuid = this._dialogConfigService.show(LegalConsentDialogComponent, 'LEGAL_CONSENT_DIALOG', data);

        this._dialogConfigService.dialogRef$.pipe(
            filter(({ uuid: u }) => u === uuid),
            first()
        ).subscribe(({ ref }) => {
            ref?.onClose.subscribe((result: any) => {
                onClose(result?.accepted === true);
            });
        });
    }

    /**
     * Empile les popups d'annonce dans l'ordre de publication : la suivante n'ouvre qu'à
     * la fermeture de la précédente.
     */
    private _showNextAnnouncement(queue: Announcement[]): void {
        const announcement = queue.shift();
        if (!announcement) {
            return;
        }

        const data: AnnouncementDialogData = { announcement: announcement };
        const uuid = this._dialogConfigService.show(
            AnnouncementDialogComponent,
            announcement.requiresAcknowledgement ? 'ANNOUNCEMENT_ACKNOWLEDGE_DIALOG' : 'ANNOUNCEMENT_DIALOG',
            data
        );

        this._dialogConfigService.dialogRef$.pipe(
            filter(({ uuid: u }) => u === uuid),
            first()
        ).subscribe(({ ref }) => {
            ref?.onClose.subscribe(() => {
                // Fermer une popup vaut lecture, quel qu'ait été le moyen de la fermer :
                // l'appel est sans effet si le dialog l'a déjà marquée à l'acquittement.
                this._announcementService.markAsRead$(announcement).subscribe(() => {
                    this._showNextAnnouncement(queue);
                });
            });
        });
    }
}
